// Eskom PARCC — editable Transaction detail (review fields + finding-line editor).
//
// The edit-mode counterpart to the read-only `TransactionDetail`. A reviewer who
// is allowed to edit (see `canEditTransaction`) toggles into this from the
// detail panel; it owns local draft state for the review fields and the finding
// lines, and commits everything on an explicit Save (no autosave).
//
// Save sequence (concurrency-guarded):
//   1. `checkRowVersion(referenceNo, loadedRowVersion)` — re-read the current
//      server rowVersion and compare to the value loaded into the editor. On
//      conflict (`{ ok:false }`) we surface a toast and refetch — we do NOT write
//      a stale edit over a newer one.
//   2. On `{ ok:true }`: `useUpsertTransaction` with the edited review fields,
//      then `useReplaceFindings` with the rebuilt finding payload. Replace-per-tx
//      is the chosen strategy (see the note on `handleSave`).
// The mutation hooks invalidate their caches on success, so the register + detail
// re-read; all mutation errors surface via the hooks' toast. Editing-layer errors
// (conflict, unexpected throw) are surfaced here, never swallowed.
import { useMemo, useState } from 'react';
import { Card, Button, Select, useToast } from '../../components/ui';
import { TRANSACTION_COMPLIANCE_LEVELS } from '../../domain/enums';
import { rollupGapStatusFromFindings } from '../../domain/gapClosure';
import { isNonCompliantFinding, closeNonCompliantFindings } from '../../domain/complianceCascade';
import { checkRowVersion } from '../../lib/concurrency';
import { adminApi } from '../../lib/adminApi';
import { useUpsertTransaction, useReplaceFindings, useSyncWorkingPaperReview } from '../../hooks';
import { diffFields, changesNote, TRANSACTION_FIELD_LABELS } from '../../lib/fieldDiff';
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import { FindingEditorCard } from '../findings/FindingEditorCard';
import {
  type FindingDraft,
  applySuggestion,
  draftsToInsertInputs,
  findingToDraft,
  newFindingDraft,
  txContextOf,
} from './editTransaction';

const REVIEWED_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
] as const;

export function TransactionEditor({
  tx,
  findings,
  onClose,
}: {
  tx: Transaction;
  /** The transaction's finding lines (already filtered by referenceNo). */
  findings: readonly FindingRead[];
  /** Leave edit mode (after a successful save, or on cancel). */
  onClose: () => void;
}): React.JSX.Element {
  const { show } = useToast();
  const upsertTransaction = useUpsertTransaction();
  const replaceFindings = useReplaceFindings();
  const syncWpReview = useSyncWorkingPaperReview();

  // Freeze the rowVersion the editor opened against — the optimistic-concurrency
  // precondition checked at Save time. This MUST be captured once at mount: a
  // background refetch can advance the SAME open transaction's `tx.rowVersion`
  // while the editor stays mounted, and if we re-read it every render the snapshot
  // would silently track the new value, making `checkRowVersion` compare new-vs-new
  // and pass — defeating the conflict guard (stale-form edits would overwrite the
  // concurrent change). The page unmounts/remounts the editor per transaction
  // (selection change closes editing), so freezing at mount is correct.
  const [loadedRowVersion] = useState(tx.rowVersion);
  const ctx = useMemo(() => txContextOf(tx), [tx]);

  // Review fields.
  const [reviewed, setReviewed] = useState<boolean>(tx.reviewed === true);
  // Seeded normalised so a legacy stored value (Unresolved/Resolved/…) matches
  // one of the four-state options instead of rendering an empty select.
  const [override, setOverride] = useState<string>(tx.complianceLevelOverride ?? '');

  // Finding drafts (local working copy of the lines).
  const [drafts, setDrafts] = useState<FindingDraft[]>(() => findings.map(findingToDraft));
  // Compliance Override cascade (owner feedback #9): setting the override to
  // Compliant offers to flip every non-compliant finding at once. Holds the count
  // to confirm, or null when there is nothing to prompt.
  const [cascadeCount, setCascadeCount] = useState<number | null>(null);

  const saving = upsertTransaction.isPending || replaceFindings.isPending || syncWpReview.isPending;

  function patchDraft(key: string, patch: Partial<FindingDraft>): void {
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }

  // Compliance Override → Compliant: offer the one-shot cascade when non-compliant
  // findings remain (owner feedback #9). Any other value dismisses the prompt —
  // the coordinator can still edit affected findings individually.
  function handleOverrideChange(value: string): void {
    setOverride(value);
    const n =
      value === 'Compliant'
        ? drafts.filter((d) => isNonCompliantFinding(d)).length
        : 0;
    setCascadeCount(n > 0 ? n : null);
  }

  function applyCascade(): void {
    // RTM Workstream I: CLOSE the non-compliant findings (keep the tier, record the
    // route) instead of relabelling them Compliant — a relabel dropped them out of
    // the register entirely; a closure keeps them identified AND resolved.
    const closedAt = new Date().toISOString();
    setDrafts((ds) => closeNonCompliantFindings(ds, { closedVia: 'override', closedAt }));
    setCascadeCount(null);
  }

  // When a finding's TEXT changes, re-run the domain classifier and pre-fill the
  // suggestion-derived metadata (without clobbering a reviewer's own edits).
  function onTextChange(key: string, text: string): void {
    setDrafts((ds) => ds.map((d) => (d.key === key ? applySuggestion(d, text, ctx) : d)));
  }

  function toggleAnchor(key: string, anchor: string, checked: boolean): void {
    setDrafts((ds) =>
      ds.map((d) => {
        if (d.key !== key) return d;
        const set = new Set(d.anchors);
        if (checked) set.add(anchor);
        else set.delete(anchor);
        return { ...d, anchors: [...set] };
      }),
    );
  }

  function addFinding(): void {
    setDrafts((ds) => [...ds, newFindingDraft()]);
  }

  function deleteFinding(key: string): void {
    setDrafts((ds) => ds.filter((d) => d.key !== key));
  }

  async function handleSave(): Promise<void> {
    // 1. Concurrency guard — narrow the last-write-wins window. On conflict,
    //    don't write the stale edit; tell the user and pull the fresh state. This
    //    runs BEFORE any mutation, so if it throws no write was attempted; surface
    //    that here (the mutation hooks aren't involved yet).
    let check;
    try {
      check = await checkRowVersion(tx.referenceNo, loadedRowVersion);
    } catch (error) {
      show(
        error instanceof Error ? error.message : 'Could not verify the transaction. Please retry.',
        'error',
      );
      return; // editor stays open so the user can retry; nothing was written
    }
    if (!check.ok) {
      show(
        check.current === null
          ? 'This transaction no longer exists. Refreshing…'
          : 'This transaction was changed by someone else. Refreshing — please reapply your edits.',
        'error',
      );
      onClose(); // re-reads from the (invalidated/fresh) cache
      return;
    }

    // 2. Write phase. The mutation hooks OWN their own error toast (via onError),
    //    so we must NOT emit a second generic toast here — a `mutateAsync`
    //    rejection has already surfaced exactly one error. We simply let the throw
    //    abort the save: the success toast / onClose below are skipped, so the
    //    editor stays open and the reviewer can retry with their edits intact.
    // The two review signals move together: the string that the WP / Findings /
    // Allocation surfaces read, and the boolean the register reads via `isReviewed`.
    // Un-review writes a DEFINITE 'Not Reviewed' (never the old stored string — that
    // was the "Reviewed with Findings" that survived an un-review).
    const reviewStatus = reviewed ? 'Reviewed' : 'Not Reviewed';
    // A full un-review with no finding lines left is a reset to Pending: blank the
    // free-text findings (concrete '' — DC leaves an omitted var unchanged) so the
    // register's effectiveCompliance no longer derives a stale non-compliant level
    // from carried-over text. The finding ROWS are already removed by 2b below.
    const reset = !reviewed && drafts.length === 0;
    // Gap status is DERIVED from the findings' lifecycle (no manual select), so the
    // persisted Transaction.gapsStatus — read by the Transactions filter, exports and
    // the detail view — always matches the dashboard's Gaps Open/Closed.
    const findingInputs = draftsToInsertInputs(drafts);
    try {
      // 2a. Save the review fields. `reviewed` (boolean) and `reviewStatus` (string)
      //     are written in lock-step; `complianceLevelOverride` is ALWAYS sent (incl.
      //     '') so choosing "No override" actually clears a stale strict-rule level —
      //     an omitted/undefined var would leave it unchanged.
      // A CHANGED override is ALSO written as the stored compliance level and mirrored onto
      // the working paper (the WP editor and the CES capture already dual-write the pair), so
      // the detail panel's "Compliance level", the Working Papers list and the exports — which
      // read the STORED level — agree with the register instead of keeping a stale
      // self-declared tier beside an override of Compliant (Lindiwe, 29 Sep 2026: 4600080858
      // "still remains as self declared"). Set → mirrored; cleared ("No override") → the
      // mirror and the paper's level are cleared too, so the findings decide again. An
      // UNTOUCHED override never touches either (a save that edited findings or the reviewed
      // flag must not overwrite a level the working paper captured).
      const overrideChanged = override !== (tx.complianceLevelOverride ?? '');
      const txInput = {
        referenceNo: tx.referenceNo,
        reviewed,
        gapsStatus: rollupGapStatusFromFindings(findingInputs) || undefined,
        complianceLevelOverride: override,
        ...(overrideChanged ? { complianceLevel: override } : {}),
        reviewStatus,
        ...(reset ? { updatedFindings: '', findingText: '', initialFindings: '' } : {}),
      };
      const txChanges = diffFields(
        tx as unknown as Record<string, unknown>,
        txInput,
        TRANSACTION_FIELD_LABELS,
      );
      await upsertTransaction.mutateAsync({
        input: txInput,
        audit: { note: changesNote(txChanges), changes: txChanges },
      });

      // 2b. Replace-per-tx for the findings. Chosen over per-finding updates
      //     because the editor supports add/delete as well as edit: a single
      //     delete-then-reinsert keeps the persisted set in lock-step with the
      //     editor (no orphan rows, no per-row id bookkeeping), and matches the
      //     write layer's `replaceFindingsForTx` wrapper exactly.
      await replaceFindings.mutateAsync({
        referenceNo: tx.referenceNo,
        findings: findingInputs,
      });

      // 2c. Mirror the review status — and a changed override — onto the working paper
      //     (no-op if none), so the Working Papers / Findings / Allocation surfaces —
      //     which read `wp.reviewStatus` / `wp.complianceLevel` — agree with the
      //     register instead of keeping a stale "Reviewed with Findings" or a stale
      //     self-declared level.
      await syncWpReview.mutateAsync({
        transactionId: tx.id,
        referenceNo: tx.referenceNo,
        reviewStatus,
        ...(overrideChanged ? { complianceLevel: override } : {}),
      });
    } catch {
      // Already toasted by the rejecting mutation hook's onError. Do not toast
      // again and do not close — the editor stays open for a retry.
      return;
    }

    // Best-effort branded notification on the transition INTO reviewed: tell the
    // admin who allocated it (recipient resolved server-side). Guarded on `!tx.reviewed`
    // so re-saving an already-reviewed transaction does not re-notify.
    if (reviewed && !tx.reviewed) {
      void adminApi.notifyReviewComplete(tx.referenceNo).catch(() => undefined);
    }

    show('Transaction saved.', 'success');
    onClose();
  }

  return (
    <Card>
      <div className="summarybox">
        <div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{tx.referenceNo}</div>
          <div className="small">Editing review fields and finding lines</div>
        </div>
        <div className="actions">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="detail-edit-grid">
        <label>
          Reviewed
          <Select
            ariaLabel="Reviewed"
            value={reviewed ? 'yes' : 'no'}
            onChange={(v) => setReviewed(v === 'yes')}
            options={REVIEWED_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
        </label>
        <label>
          Compliance override
          <Select
            ariaLabel="Compliance override"
            value={override}
            onChange={handleOverrideChange}
            options={[
              { value: '', label: 'No override' },
              ...TRANSACTION_COMPLIANCE_LEVELS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
      </div>
      {cascadeCount !== null && (
        <div className="ces-cascade-prompt" role="status">
          <span>
            Set all {cascadeCount} non-compliant finding{cascadeCount === 1 ? '' : 's'} to Compliant?
            You can still adjust individual findings afterwards.
          </span>
          <span className="ces-cascade-actions">
            <Button onClick={applyCascade} disabled={saving}>
              Set all Compliant
            </Button>
            <Button variant="ghost" onClick={() => setCascadeCount(null)} disabled={saving}>
              Keep individual
            </Button>
          </span>
        </div>
      )}

      <div>
        <div className="section-head" style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700 }}>
            Findings{drafts.length ? ` (${drafts.length})` : ''}
          </div>
          <Button onClick={addFinding} disabled={saving}>
            Add finding
          </Button>
        </div>
        {drafts.length === 0 ? (
          <div className="empty">No finding lines. Use “Add finding” to record one.</div>
        ) : (
          drafts.map((d) => (
            <FindingEditorCard
              key={d.key}
              draft={d}
              disabled={saving}
              onText={(text) => onTextChange(d.key, text)}
              onPatch={(patch) => patchDraft(d.key, patch)}
              onToggleAnchor={(anchor, checked) => toggleAnchor(d.key, anchor, checked)}
              onDelete={() => deleteFinding(d.key)}
            />
          ))
        )}
      </div>
    </Card>
  );
}
